import { unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type CustomerListItem = Schemas['CustomerListItemDto'];
export type Customer = Schemas['CustomerDto'];
export type SaveCustomerRequest = Schemas['SaveCustomerRequest'];
export type CustomerKind = Schemas['CustomerKindDto'];
export type Salutation = Schemas['SalutationDto'];
export type CustomerNote = Schemas['CustomerNoteDto'];
export interface CustomerFilter {
  q?: string | undefined;
  include_archived?: boolean | undefined;
}

const keys = {
  all: ['crm'] as const,
  customers: (f: CustomerFilter) => ['crm', 'customers', f] as const,
  customer: (id: string) => ['crm', 'customer', id] as const,
  notes: (id: string) => ['crm', 'customer', id, 'notes'] as const,
};

function invalidate(client: QueryClient) {
  return client.invalidateQueries({ queryKey: keys.all });
}

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

export function useCustomers(filter: CustomerFilter = {}, enabled = true) {
  return useQuery({
    queryKey: keys.customers(filter),
    queryFn: async () =>
      unwrap(
        await api.GET('/customers', {
          params: {
            query: {
              ...(filter.q ? { q: filter.q } : {}),
              include_archived: filter.include_archived ?? false,
            },
          },
        }),
      ),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useCustomer(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.customer(id),
    queryFn: async () =>
      unwrap(await api.GET('/customers/{customer_id}', { params: { path: { customer_id: id } } })),
    enabled: enabled && id !== '',
  });
}

export function useSaveCustomer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id?: string | undefined;
      body: SaveCustomerRequest;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/customers/{customer_id}', {
              params: { path: { customer_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/customers', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useCustomerAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: 'archive' | 'unarchive' | 'anonymize';
    }) => {
      const path = { params: { path: { customer_id: id } } };
      switch (action) {
        case 'archive':
          return unwrap(await api.POST('/customers/{customer_id}/archive', path));
        case 'unarchive':
          return unwrap(await api.POST('/customers/{customer_id}/unarchive', path));
        case 'anonymize':
          return unwrap(await api.POST('/customers/{customer_id}/anonymize', path));
      }
    },
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteCustomer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE('/customers/{customer_id}', { params: { path: { customer_id: id } } }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCustomerNotes(id: string) {
  return useQuery({
    queryKey: keys.notes(id),
    queryFn: async () =>
      unwrap(
        await api.GET('/customers/{customer_id}/notes', { params: { path: { customer_id: id } } }),
      ),
  });
}

export function useSaveCustomerNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerId,
      id,
      text,
      noted_on,
      version,
    }: {
      customerId: string;
      id?: string | undefined;
      text: string;
      noted_on?: string | undefined;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/customer-notes/{note_id}', {
              params: { path: { note_id: id } },
              body: { text, noted_on: noted_on ?? null },
              headers: ifMatch(version),
            }),
          )
        : unwrap(
            await api.POST('/customers/{customer_id}/notes', {
              params: { path: { customer_id: customerId } },
              body: { text, noted_on: noted_on ?? null },
            }),
          ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteCustomerNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.DELETE('/customer-notes/{note_id}', { params: { path: { note_id: id } } })),
    onSuccess: () => invalidate(client),
  });
}
